import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronRight } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import heroImage from "@/assets/hero-image.png";
import heroTracking from "@/assets/hero-tracking.jpg";
import securityBg from "@/assets/security-bg.jpg";

const stats = [
  { value: "500+", label: "OFFICERS TRACKED" },
  { value: "50+", label: "SITES PROTECTED" },
  { value: "99.8%", label: "UPTIME" },
];

const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-screen overflow-hidden pt-20">
      {/* Security visuals background — stronger overlay on mobile for readability */}
      <div className="absolute inset-0">
        <img src={securityBg} alt="Security personnel monitoring surveillance systems in a proactive digital fortress" className="h-full w-full object-cover object-center" loading="eager" />
        <div className="absolute inset-0 bg-background/50 sm:bg-background/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/40 to-background/90" />
      </div>

      <div className="relative mx-auto flex max-w-7xl flex-col items-center gap-10 px-6 py-12 sm:gap-12 sm:py-20 lg:flex-row lg:items-center lg:py-28">
        <div className="flex-1 space-y-8 animate-fade-in text-center lg:text-left">
          <h1 className="text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl md:text-5xl lg:text-7xl">
            SECUREFLEX:<br />
            <span className="text-gradient-gold">PROACTIVE</span> DIGITAL FORTRESS
          </h1>
          <p className="mx-auto max-w-lg text-base text-muted-foreground sm:text-lg lg:mx-0">
            The world's most advanced security patrol management platform.
            Real-time GPS tracking, AI-powered incident analysis, and biometric
            checkpoint verification — built for modern security operations.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center sm:gap-4 lg:justify-start">
            <Button size="lg" asChild className="w-full h-14 sm:h-12 gap-2 rounded-full text-base sm:w-auto">
              <Link to="/app">
                Go to SecureFlex App <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" asChild className="w-full h-14 sm:h-12 gap-2 rounded-full text-base sm:w-auto" variant="outline">
              <Link to="/app/signup">
                Start Free 30-Day Trial <ChevronRight className="h-5 w-5" />
              </Link>
            </Button>
          </div>
          <div className="flex flex-wrap justify-center gap-6 sm:gap-10 pt-4 lg:justify-start">
            {stats.map((stat) => (
              <div key={stat.label} className="flex flex-col items-center lg:items-start text-center lg:text-left">
                <p className="text-2xl font-bold text-primary sm:text-3xl md:text-4xl">{stat.value}</p>
                <p className="text-[10px] font-bold tracking-widest text-muted-foreground uppercase">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 w-full max-w-xl mx-auto flex justify-center animate-fade-in lg:max-w-none" style={{ animationDelay: "0.3s" }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
            <div className="relative group">
              <div className="absolute -inset-3 rounded-2xl bg-gradient-to-br from-primary/20 via-transparent to-primary/10 blur-2xl opacity-50 group-hover:opacity-100 transition-opacity" />
              <img src={heroImage} alt="SecureFlex security command center dashboard showing real-time GPS tracking and patrol management analytics" className="relative w-full rounded-2xl border border-border/50 shadow-2xl shadow-primary/10 aspect-[4/5] object-cover" loading="lazy" />
              <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-background/90 backdrop-blur-md px-4 py-3 border border-border/50 shadow-xl">
                <p className="text-[10px] font-bold tracking-[0.2em] text-primary">COMMAND CENTER</p>
                <p className="text-xs text-muted-foreground mt-0.5">Live monitoring & analytics</p>
              </div>
            </div>
            <div className="relative group hidden sm:block">
              <div className="absolute -inset-3 rounded-2xl bg-gradient-to-br from-accent/20 via-transparent to-primary/10 blur-2xl opacity-50 group-hover:opacity-100 transition-opacity" />
              <img src={heroTracking} alt="Real-time officer GPS tracking on a digital map for verified security patrol operations" className="relative w-full rounded-xl border border-border/50 shadow-2xl shadow-accent/10 aspect-[4/5] object-cover" loading="lazy" />
              <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-background/90 backdrop-blur-md px-4 py-3 border border-border/50 shadow-xl">
                <p className="text-[10px] font-bold tracking-[0.2em] text-primary">OFFICER TRACKING</p>
                <p className="text-xs text-muted-foreground mt-0.5">Live GPS patrol routes</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden border-y border-border/50 bg-secondary/30 py-4">
        <div className="flex whitespace-nowrap animate-[marquee_30s_linear_infinite]">
          {Array(2).fill(null).map((_, i) => (
            <span key={i} className="mx-4 text-sm font-semibold tracking-widest text-muted-foreground">
              SECUREFLEX · PROACTIVE DIGITAL FORTRESS · BIOMETRIC CHECK-IN · AI INCIDENT ANALYSIS · HOURLY SCHEDULING · GPS PATROL TRACKING ·{" "}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
